import { createSlice } from '@reduxjs/toolkit';

// Helper functions for localStorage
const loadCartFromStorage = () => {
  try {
    const serializedCart = localStorage.getItem('pediPiggyCart');
    if (serializedCart === null) {
      return { items: [], total: 0, itemCount: 0 };
    }
    return JSON.parse(serializedCart);
  } catch (err) {
    console.warn('Could not load cart from localStorage:', err);
    return { items: [], total: 0, itemCount: 0 };
  }
};

const saveCartToStorage = (cartState) => {
  try {
    const serializedCart = JSON.stringify(cartState);
    localStorage.setItem('pediPiggyCart', serializedCart);
  } catch (err) {
    console.warn('Could not save cart to localStorage:', err);
  }
};

const calculateCartTotals = (items) => {
  const total = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  return { total: parseFloat(total.toFixed(2)), itemCount };
};

// Initial state - load from localStorage if available
const initialState = loadCartFromStorage();

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const { product, quantity = 1 } = action.payload;
      const existingItemIndex = state.items.findIndex(item => item.id === product.id);
      
      if (existingItemIndex >= 0) {
        // Update quantity if item already exists
        state.items[existingItemIndex].quantity += quantity;
      } else {
        // Add new item to cart
        state.items.push({
          id: product.id,
          title: product.title,
          price: product.price,
          condition: product.condition,
          quantity: quantity,
          seller: product.seller,
          primary_image: product.primary_image,
          is_available: product.is_available,
          stock_quantity: product.quantity, // Available stock
          addedAt: new Date().toISOString()
        });
      }
      
      // Recalculate totals
      const totals = calculateCartTotals(state.items);
      state.total = totals.total;
      state.itemCount = totals.itemCount;
      
      // Save to localStorage
      saveCartToStorage(state);
    },
    
    removeFromCart: (state, action) => {
      const productId = action.payload;
      state.items = state.items.filter(item => item.id !== productId);
      
      // Recalculate totals
      const totals = calculateCartTotals(state.items);
      state.total = totals.total;
      state.itemCount = totals.itemCount;
      
      // Save to localStorage
      saveCartToStorage(state);
    },
    
    updateCartItemQuantity: (state, action) => {
      const { productId, quantity } = action.payload;
      const itemIndex = state.items.findIndex(item => item.id === productId);
      
      if (itemIndex >= 0) {
        if (quantity <= 0) {
          // Remove item if quantity is 0 or less
          state.items.splice(itemIndex, 1);
        } else {
          // Update quantity
          state.items[itemIndex].quantity = quantity;
        }
        
        // Recalculate totals
        const totals = calculateCartTotals(state.items);
        state.total = totals.total;
        state.itemCount = totals.itemCount;
        
        // Save to localStorage
        saveCartToStorage(state);
      }
    },
    
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
      state.itemCount = 0;
      
      // Clear localStorage
      localStorage.removeItem('pediPiggyCart');
    },
    
    // Action to sync cart with authentication state
    syncCartWithAuth: (state, action) => {
      const { isAuthenticated, userId } = action.payload;
      
      if (isAuthenticated && userId) {
        // TODO: In future, sync with backend cart API
        // For now, just keep the local cart
        console.log('User authenticated - cart will sync with backend in future');
      } else {
        // Guest user - continue using localStorage
        console.log('Guest user - using localStorage cart');
      }
    },
    
    // Load cart from storage (useful for app initialization)
    loadCart: (state) => {
      const savedCart = loadCartFromStorage();
      state.items = savedCart.items;
      state.total = savedCart.total;
      state.itemCount = savedCart.itemCount;
    }
  }
});

export const {
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
  syncCartWithAuth,
  loadCart
} = cartSlice.actions;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotal = (state) => state.cart.total;
export const selectCartItemCount = (state) => state.cart.itemCount;
export const selectCartItemById = (state, productId) => 
  state.cart.items.find(item => item.id === productId);

export default cartSlice.reducer;